import { track } from "shared/boot/analytics";
import { $streemApiV1 } from "shared/boot/api";
import useTeamPicker from "shared/composables/useTeamPicker";
import { getTimezone } from "shared/helpers/date";
import DateRange from "shared/helpers/DateRange";
import {
  type ExclusionResults,
  getExcludedKeys,
} from "shared/helpers/excludedMentionsHelper";
import { SortOptionField, SortOptionOrder } from "shared/helpers/mentions";
import {
  type StreamReportFilters,
  type StreamRequestFilters,
} from "shared/helpers/StreamFilters";
import {
  type ChartExportFormat,
  type SavedChartSetting,
} from "shared/modules/Charts/types";
import {
  type ChartExportParams,
  type StreamReportFormat,
} from "shared/modules/Insights/types";
import PublicInsightsService from "shared/services/api/PublicInsightsService";
import features from "shared/services/features";
import { type Nullable } from "shared/types";
import {
  type Mention,
  type MentionType,
  type SocialMentionType,
  type SyndicationKey,
} from "shared/types/mentions";

export type StreamReportMention = Mention & {
  selected?: boolean;
};

export type ReportParamsOptionsChartSetting = Omit<
  SavedChartSetting,
  "advanced_filters"
> & {
  advanced_filters?: Nullable<StreamRequestFilters>;
};

export interface ReportParamsOptions {
  collapse_syndicated?: boolean;
  document_ids?: SyndicationKey[];
  instant_insights_report_id?: number | string;
  new_chart_settings: ReportParamsOptionsChartSetting[];
}

export interface ReportParams<Options = ReportParamsOptions> {
  bundleBroadcast?: boolean;
  collapseSyndicated?: boolean;
  csv?: boolean;
  customLabel?: string;
  displayCharts?: boolean;
  displayMentions?: boolean;
  documentIds?: SyndicationKey[];
  filterSetId?: number;
  format: ChartExportFormat | StreamReportFormat;
  includePrintClipsPdf?: boolean;
  language?: string;
  mentions?: Nullable<StreamReportMention[]>;
  note?: string;
  options: Options;
  organisationBrandId?: number;
  pdf?: boolean;
  range: DateRange;
  reportType?: string;
  showRestrictedContent?: boolean;
  signal?: AbortSignal;
  sortBy?: SortOptionField;
  sortOrder?: SortOptionOrder;
  stockSymbol?: string;
  stream?: Nullable<{ id: number; label?: string }>;
  summary?: string;
  timeZone?: string;
  types?: (MentionType | SocialMentionType)[];
}

export interface TaggedReportParamsContentItem {
  id: number;
  keywords: string[];
  type: MentionType;
}

export interface TaggedReportParamsContent {
  items: TaggedReportParamsContentItem[];
  label: string;
  order: number;
}

export interface TaggedReportParams {
  after: number;
  before: number;
  body?: string;
  content: TaggedReportParamsContent[];
  format: string;
  includeCompletePDF: boolean;
  language?: string;
  organisationBrandId?: Nullable<number>;
  recipients: string[];
  sendAsOrganisation: boolean;
  showExcerpts: boolean;
  showRestrictedContent?: boolean;
  subject?: string;
}

function generateReportParams<Options = ReportParamsOptions>({
  bundleBroadcast = false,
  collapseSyndicated = false,
  csv,
  customLabel,
  displayCharts,
  displayMentions,
  documentIds,
  filterSetId,
  format,
  includePrintClipsPdf,
  language,
  mentions = [],
  note,
  options,
  organisationBrandId,
  pdf,
  range,
  reportType,
  showRestrictedContent,
  sortBy = SortOptionField.TIMESTAMP,
  sortOrder = SortOptionOrder.DESC,
  stockSymbol,
  stream,
  summary,
  timeZone,
  types,
}: ReportParams<Options>): ChartExportParams<Options> {
  const { selectedTeamPreference } = useTeamPicker();

  const computedRange = range.range?.label
    ? DateRange.rangeForLabel(range.range.label)
    : range;

  // If documentIds is provided, include it in options instead of calculating excluded items
  let exclusionParams = {};
  let optionsWithDocumentIds = options;

  if (documentIds && documentIds.length > 0) {
    optionsWithDocumentIds = {
      ...options,
      document_ids: documentIds,
    };
  } else {
    const sanitizedMentions = mentions || []; // null can be passed which overrides the default array

    const unselectedMentions = sanitizedMentions.filter(
      ({ selected }) => !selected
    );

    const excludedMentions: ExclusionResults = getExcludedKeys(
      unselectedMentions,
      bundleBroadcast,
      collapseSyndicated
    );

    exclusionParams = {
      excluded_items: excludedMentions.excludedItemIds,
      excluded_syndication_keys: excludedMentions.excludedSyndicationKeys,
      excluded_time_bundle_keys: excludedMentions.excludedTimeBundleKeys,
    };
  }

  const params: ChartExportParams<Options> = {
    after: computedRange.after,
    before: computedRange.before,
    bundle_broadcast: bundleBroadcast,
    collapse_syndicated: collapseSyndicated,
    csv,
    display_charts: displayCharts,
    display_mentions: displayMentions,
    ...exclusionParams,
    format,
    include_print_clips_pdf: includePrintClipsPdf,
    label: customLabel || stream?.label,
    note,
    options: optionsWithDocumentIds,
    pdf,
    report_type: reportType,
    show_restricted_content: showRestrictedContent,
    sort_by: sortBy,
    sort_order: sortOrder,
    stock_symbol: stockSymbol,
    stream_id: stream?.id,
    time_zone: timeZone || getTimezone(),
    types,
    ...(features.has("has_team_workspaces")
      ? { organisation_team_id: selectedTeamPreference.value }
      : {}),
    filter_set_id: filterSetId,
    language,
    organisation_brand_id: organisationBrandId,
    summary,
  };

  return params;
}

function getStreamReportFormatSettings(type: StreamReportFormat) {
  const reportTypes = {
    csv: { csv: true, format: "csv", pdf: false },
    pdf: { csv: false, format: "pdf", pdf: true },
  };

  return reportTypes[type];
}

function individualChartReport(
  params: ReportParams,
  {
    isPublic = false,
    publicAccessKey = "",
  }: { isPublic?: boolean; publicAccessKey?: string } = {}
) {
  const reportParams = generateReportParams(params);

  const chart = params.options.new_chart_settings[0];

  track("Individual Chart Export", {
    after: params.range.after,
    before: params.range.before,
    chart: chart.chart,
    format: params.format,
    media_release_ids: chart.media_release_ids,
    stream_ids: chart.stream_ids,
  });

  if (isPublic && publicAccessKey) {
    // Use the public insights service for chart exports in public contexts
    const publicInsightsService = new PublicInsightsService(publicAccessKey);

    return publicInsightsService.chartExport(reportParams);
  }

  return $streemApiV1.post("chart_export", {
    params: reportParams,
    responseType: "arraybuffer",
  });
}

function sendTaggedReportEmail({
  after,
  before,
  body,
  content,
  format,
  includeCompletePDF,
  language,
  organisationBrandId,
  recipients,
  sendAsOrganisation,
  showExcerpts,
  showRestrictedContent,
  subject,
}: TaggedReportParams) {
  track("Sent Email Report in Tagged Mentions Report Modal");

  return $streemApiV1
    .post("tagged_mentions_reports", {
      params: {
        content,
        recipients,
        ...(subject ? { subject } : {}),
        ...(body ? { message: body } : {}),
        after,
        before,
        format,
        include_complete_pdf: includeCompletePDF,
        language,
        organisation_brand_id: organisationBrandId,
        send_as_organisation: sendAsOrganisation,
        show_excerpts: showExcerpts,
        show_restricted_content: showRestrictedContent,
      },
    })
    .then((response) => response.data.success);
}

function streamReport<
  Options extends ReportParamsOptions | StreamReportFilters,
>(params: ReportParams<Options>) {
  const reportParams = generateReportParams(params);

  track("Built Report", {
    after: params.range.after,
    before: params.range.before,
    format: params.format,
    stream_id: params.stream?.id,
    type: params.reportType,
  });

  return $streemApiV1
    .post("stream_reports", {
      params: reportParams,
      signal: params.signal,
    })
    .then((response) => response.data);
}

export {
  generateReportParams,
  getStreamReportFormatSettings,
  individualChartReport,
  sendTaggedReportEmail,
  streamReport,
};
